import { isResource } from '@medplum/core'
import type {
  Basic,
  Coding,
  Organization,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import {
  BASIC_CODE_SYSTEM,
  TASK_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
} from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { Label } from '#src/models/label.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class TaskLabel extends Label {
  static isInstance(value: unknown): value is Basic {
    return (
      isResource(value) &&
      isOfType(value, 'Basic') &&
      hasCodeableConcept(value.code, codeableConcept(TaskLabel.code))
    )
  }

  constructor({
    name,
    color,
    author,
    systemManagedId,
  }: {
    name: string
    color: string
    author: Reference<Practitioner | Organization>
    systemManagedId?: string
  }) {
    super({
      code: codeableConcept(TaskLabel.code),
      name,
      color,
      author,
    })
    if (systemManagedId) {
      this.systemManagedId = systemManagedId
    }
  }

  @Tag({
    system: TASK_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
  })
  _systemManagedId?: Coding

  get isSystemManaged(): boolean {
    return !!this._systemManagedId?.code
  }

  get systemManagedId(): string | undefined {
    return this._systemManagedId?.code
  }

  set systemManagedId(code: string) {
    this._systemManagedId = { code }
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      code: TaskLabel.code,
      _tag: Label.status.active,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static bySystemManagedId(systemManagedId: string): QueryTypes {
    return {
      ...TaskLabel.baseSearchQuery(),
      _tag: {
        system: TASK_LABEL_SYSTEM_MANAGED_ID_SYSTEM,
        code: systemManagedId,
      },
    }
  }

  static code = {
    system: BASIC_CODE_SYSTEM,
    code: 'task-label',
    display: 'Task Label',
  }
}
